<template>
  <div v-if="showBio" class="sp-finder-result-location-actions --layout-bio" part="base">
    <sp-icon class="sp-finder-result-location-actions__icon" name="map-pin-filled"></sp-icon>
    <span class="sp-finder-result-location-actions__text">
      <sp-button
        v-if="showMapLink"
        class="sp-finder-result-location-actions__link"
        variant="appearance-link"
        :data-testid="TestId.finder.showOnMap"
        @click="handleShowMap"
        >{{ t("show_on_map") }}</sp-button
      >
      <template v-if="showSeparator"> | </template>
      <a
        v-if="showOtherLocationsLink"
        class="sp-finder-result-location-actions__other-locations"
        :href="result.url"
        :title="otherLocationsTitle"
        >{{ t("results.more_locations") }}</a
      >
    </span>
  </div>
  <div v-else-if="showDirectory" class="sp-finder-result-location-actions" part="base">
    <!-- TODO(parity): legacy "Directions" link dropped for the CHRISTUS redesign; restore for cross-client parity. -->
    <div v-if="hasAddressActions" class="sp-finder-result-location-actions__actions">
      <!-- The inline variant keeps the link's text baseline, so the distance lines up with it. -->
      <sp-button
        v-if="showMapLink"
        class="sp-finder-result-location-actions__link"
        variant="appearance-link-inline"
        type="button"
        @click="handleShowMap"
        >{{ t("show_on_map") }}</sp-button
      >
      <span v-if="result.distance" class="sp-finder-result-location-actions__distance">{{ distanceLabel }}</span>
    </div>

    <sp-tile
      v-if="showMoreLocationsTile"
      class="sp-finder-result-location-actions__tile sp-finder-result-location-actions__tile--more-locations"
      icon="map-pin-filled"
      layout="row"
      align="center"
      size="small"
      type="body-bold"
      :href="moreLocationsHref"
      :label="moreLocationsLabel"
    ></sp-tile>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { TestId } from "#/test-ids";
import { trackControlClick } from "#ce/components/SpFinder/lib/click-analytics";
import { i18n } from "#ce/components/SpFinder/i18n";

// Direct instance access (see SpFinder/i18n.js) — useI18n() can't resolve
// the plugin across the custom-element app boundary.
function t(...args) {
  return i18n.global.t(...args);
}

const emit = defineEmits(["show-map"]);

const props = defineProps({
  layout: {
    type: String,
    default: "directory",
    validator: (value) => ["bio", "directory"].includes(value),
  },
  result: {
    type: Object,
    default: () => ({}),
  },
  resultConfig: {
    type: Object,
    default: undefined,
  },
  /** The card folds the other locations out, so the bio layout drops its link to the profile's locations. */
  foldsOutOtherLocations: {
    type: Boolean,
    default: false,
  },
});

const isPlaceholderAddress = computed(() => props.result.placeholder_address);
const isVirtualAddress = computed(() => props.result.virtual_address);
const hasPhysicalAddress = computed(() => !isPlaceholderAddress.value && !isVirtualAddress.value);

const showMapLink = computed(() => props.resultConfig?.showMapLink);
// TODO(parity): CHRISTUS collapses the legacy per-count "More Locations" variants into one generic tile; restore descriptive copy for cross-client parity.
const moreLocationsCount = computed(
  () =>
    Number(props.result.other_online_booking_address_count) ||
    Number(props.result.other_accepting_patients_address_count) ||
    Number(props.result.other_address_count) ||
    0,
);
const showMoreLocationsTile = computed(
  () => hasPhysicalAddress.value && moreLocationsCount.value > 0 && Boolean(props.result.url),
);
const hasAddressActions = computed(
  () => hasPhysicalAddress.value && Boolean(props.result.distance || showMapLink.value),
);
const hasContent = computed(() => hasAddressActions.value || showMoreLocationsTile.value);
const distanceLabel = computed(() => t("distance_away", { distance: props.result.distance }));
const moreLocationsLabel = computed(() =>
  t("more_locations", { count: moreLocationsCount.value }, moreLocationsCount.value),
);
const moreLocationsHref = computed(() => `${props.result.url}#locations`);

const isBio = computed(() => props.layout === "bio");

// The count names the most useful kind of other location: "2 other locations with online booking".
const otherLocationsTitle = computed(() => {
  const result = props.result;
  if (!result.url) {
    return "";
  }
  const kinds = [
    [result.other_online_booking_address_count, "other_locations_online_booking"],
    [result.other_accepting_patients_address_count, "other_locations_accepting_patients"],
    [result.other_address_count, "other_locations"],
  ];
  const [count, key] = kinds.find(([kindCount]) => Number(kindCount) > 0) ?? [];
  if (!count) {
    return "";
  }
  return t(`results.${key}`, { count: Number(count) }, Number(count));
});
const showOtherLocationsLink = computed(() => Boolean(otherLocationsTitle.value) && !props.foldsOutOtherLocations);
const showBio = computed(() => isBio.value && Boolean(showMapLink.value || showOtherLocationsLink.value));
const showSeparator = computed(() => Boolean(showMapLink.value && showOtherLocationsLink.value));
const showDirectory = computed(() => !isBio.value && hasContent.value);

function handleShowMap(event) {
  event.preventDefault();
  trackControlClick(event);
  emit("show-map", { resultGuid: props.result.resultGUID });
}
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
@use "@ce/components/SpFinder/styles/tile-row" as *;

.sp-finder-result-location-actions {
  /* Layout */
  --location-actions-gap: var(--sp-comp-finder-result-location-actions-gap, var(--sp-ref-scale-12));

  /* Color */
  --location-actions-link-color: var(--sp-comp-finder-result-location-actions-link-color, var(--sp-sys-color-primary));
  --location-actions-distance-color: var(
    --sp-comp-finder-result-location-actions-distance-color,
    var(--sp-ref-palette-text-light)
  );

  /* Typography */
  --location-actions-link-font-weight: var(
    --sp-comp-finder-result-location-actions-link-font-weight,
    var(--sp-sys-font-weight-bold)
  );
  --location-actions-distance-font-size: var(
    --sp-comp-finder-result-location-actions-distance-font-size,
    var(--sp-ref-font-size-small)
  );
  --location-actions-distance-font-style: var(--sp-comp-finder-result-location-actions-distance-font-style, italic);
  --location-actions-distance-font-weight: var(
    --sp-comp-finder-result-location-actions-distance-font-weight,
    var(--sp-sys-font-weight-normal)
  );
  --location-actions-distance-line-height: var(
    --sp-comp-finder-result-location-actions-distance-line-height,
    var(--sp-ref-line-height-small)
  );

  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--location-actions-gap);
  font: var(--sp-sys-typescale-body-short, inherit);

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--location-actions-gap);
  }

  &__link {
    --sp-comp-button-appearance-link-text-color: var(--location-actions-link-color);
    --sp-comp-button-font-weight: var(--location-actions-link-font-weight);

    font-weight: var(--location-actions-link-font-weight);
    --sp-comp-button-appearance-link-text-decoration: var(
      --sp-comp-finder-result-location-actions-link-text-decoration,
      none
    );

    &:hover,
    &:focus-visible {
      --sp-comp-button-appearance-link-text-decoration: underline;
    }
  }

  &__distance {
    color: var(--location-actions-distance-color);
    font-size: var(--location-actions-distance-font-size);
    font-style: var(--location-actions-distance-font-style);
    font-weight: var(--location-actions-distance-font-weight);
    line-height: var(--location-actions-distance-line-height);
  }

  &.--layout-bio {
    flex-direction: row;
    align-items: center;
    gap: var(--sp-comp-finder-result-location-actions-gap, 5px);
    min-block-size: 1lh;
    margin: var(--sp-comp-finder-result-location-actions-margin, 0 0 7px);
    font: inherit;
  }

  &__icon {
    --sp-comp-icon-size: var(--sp-comp-finder-result-location-actions-icon-size, 1.3em);
  }

  &__text {
    flex: 1;
  }

  &.--layout-bio &__link {
    --sp-comp-button-font-short: var(--sp-sys-typescale-link-font-weight) var(--sp-sys-typescale-body-font-size) / 1.5
      var(--sp-sys-typescale-link-font-family);
    --sp-comp-button-display: inline;
  }

  &__other-locations {
    color: var(--location-actions-link-color);
    font-family: var(--sp-sys-typescale-link-font-family);
    font-weight: var(--sp-sys-typescale-link-font-weight);
    text-decoration: var(--sp-comp-finder-result-location-actions-link-text-decoration, none);
  }

  &__tile {
    @include finder-tile-row;

    --sp-comp-tile-gap: var(--sp-ref-scale-8);
    --sp-comp-tile-gap-sm: var(--sp-ref-scale-8);
  }

  &__tile--more-locations {
    --sp-comp-tile-inline-size: fit-content;
    --sp-comp-tile-max-inline-size: 100%;
    --sp-comp-tile-label-text-decoration-hover: underline;
    --sp-comp-tile-icon-fill: var(--location-actions-link-color);
    --sp-comp-tile-label-color: var(--location-actions-link-color);
    --sp-comp-tile-label-color-hover: var(--location-actions-link-color);
  }
}
</style>
